import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {ezprOrange} from '../../_helpers';
import {requestStores} from '../../ListOfPREditors.brick.jsx';
import SendIcon from '@mui/icons-material/Send';
import LoadingButton from '@mui/lab/LoadingButton';
import LinearProgress from '@mui/material/LinearProgress';
import {orangeTheme} from '../../_theme';
import {ThemeProvider} from '@mui/material';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import {PurchaseRequestController} from '../../../controllers/PurchaseRequestController';
import {BrickContext} from 'olympe';
import {useProperty} from '@olympeio/core';
import {SUBMISSION_STATUS} from '../../../utils/constants';

/**
 * @param {SaveSubmitBtn.propTypes} props
 * @return {JSX.Element}
 */
export function SaveSubmitBtn(props) {
    const useRequestStore = requestStores.get(props.prTag);
    const requester = useRequestStore((state) => state.requester);
    const spender = useRequestStore((state) => state.spender);
    const actionButtonState = useRequestStore((state) => state.actionButtonState);
    const startActionButton = useRequestStore((state) => state.startActionButton);
    const status = useRequestStore((state) => state.status);

    const submissionStatus = useProperty(props.context, 'Submission status', false);

    const [showButtons, setShowButtons] = useState({
        saveBtn: false,
        submitBtn: false,
        submitToRequesterBtn: false,
        withdrawnBtn: false,
        rejectBtn: false,
    });

    const disableButtons = props.disabledButtons.save || props.disabledButtons.submit;

    const btnStyle = {
        backgroundColor: disableButtons ? 'rgb(200, 200, 200)' : ezprOrange,
        color: 'white',
        borderColor: disableButtons ? 'rgb(200, 200, 200)' : ezprOrange,
        marginBottom: '2vh',
        paddingRight: disableButtons ? '40px' : '15px',
    };

    // Here we check which buttons should be displayed.
    // No need to check authorization, as this component is not rendered if the user doesn't have authorization
    // No need to check the "Submitted" status, for the same reason.
    useEffect(() => {
        setShowButtons(PurchaseRequestController.getAvailableActions(spender, requester, status));
    }, [status, requester, spender]);

    // Disable immediately the button that is clicked, to avoid multiple clicks.
    const handleClickOnButton = (btnToDisable, functionToTrigger) => {
        props.setDisabledButtons({
            ...props.disabledButtons,
            [btnToDisable]: true,
        });
        functionToTrigger();
    };

    return (
        <>
            <div translate={'no'} className={'pr-editor-buttons'}>
                {showButtons.rejectBtn &&
                    <LoadingButton
                        style={btnStyle}
                        variant={'outlined'}
                        onClick={() => handleClickOnButton('save', props.handleClickOnReject)}
                        loading={props.disabledButtons.save}
                        loadingPosition={'end'}
                        // Use empty div to prevent getting warnings.
                        endIcon={<div />}
                        disabled={disableButtons}
                    >
                        Reject
                    </LoadingButton>
                }
                {showButtons.saveBtn &&
                <LoadingButton
                    data-cy={'Save_button'}
                    style={btnStyle}
                    variant={'outlined'}
                    onClick={() => handleClickOnButton('save', props.handleClickOnSave)}
                    loading={props.disabledButtons.save}
                    loadingPosition={'end'}
                    // Use empty div to prevent getting warnings.
                    endIcon={<div />}
                    disabled={disableButtons}
                >
                    Save
                </LoadingButton>
                }
                {showButtons.submitBtn &&
                    <LoadingButton
                        data-cy={'Submit_button'}
                        style={btnStyle}
                        variant={'outlined'}
                        onClick={() => {
                            startActionButton('Validating the PR...', 5);
                            handleClickOnButton('submit', props.handleClickOnSubmit);
                        }}
                        endIcon={<SendIcon />}
                        loading={props.disabledButtons.submit}
                        loadingPosition={'end'}
                        disabled={disableButtons}
                    >
                        Submit
                    </LoadingButton>
                }
                {showButtons.submitToRequesterBtn &&
                    <LoadingButton
                        data-cy={'Submit_to_Requester_button'}
                        style={btnStyle}
                        variant={'outlined'}
                        onClick={() => handleClickOnButton('save', props.handleClickOnSubmitToRequester)}
                        loading={props.disabledButtons.save}
                        loadingPosition={'end'}
                        endIcon={<SendIcon />}
                        disabled={disableButtons}
                    >
                        Submit to Requester
                    </LoadingButton>
                }
                {showButtons.withdrawnBtn &&
                    <LoadingButton
                        style={btnStyle}
                        variant={'outlined'}
                        onClick={() => handleClickOnButton('save', props.handleClickOnWithdrawn)}
                        loading={props.disabledButtons.save}
                        loadingPosition={'start'}
                        startIcon={<ArrowLeftIcon />}
                        disabled={disableButtons}
                    >
                        Withdrawn
                    </LoadingButton>
                }
            </div>
            {actionButtonState.loading &&
                <>
                    <div className={'pr-editor-buttons-loading'}>{SUBMISSION_STATUS[submissionStatus]}</div>
                    <ThemeProvider theme={orangeTheme}>
                        <LinearProgress variant={'determinate'} value={Math.trunc(100 * Number(submissionStatus.slice(0, 1)) / Object.keys(SUBMISSION_STATUS).length)} style={{width: '100%'}}/>
                    </ThemeProvider>
                </>
            }
        </>
    );
}

SaveSubmitBtn.propTypes = {
    context: PropTypes.instanceOf(BrickContext).isRequired,
    prTag: PropTypes.string.isRequired,
    handleClickOnSave: PropTypes.func.isRequired,
    handleClickOnSubmit: PropTypes.func.isRequired,
    handleClickOnSubmitToRequester: PropTypes.func.isRequired,
    handleClickOnWithdrawn: PropTypes.func.isRequired,
    handleClickOnReject: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    disabledButtons: PropTypes.object,
    setDisabledButtons: PropTypes.func,
};
